import React, { useState, useRef } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Modal } from 'react-native';
import { useTheme } from '../../../../theme/useTheme';
import { AdminColors } from '../../constants/adminColors';
import FontFamily from '../../../../constants/fonts';

export interface LocationFormSelectProps {
  placeholder?: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
  hasError?: boolean;
  disabled?: boolean;
}

const LocationFormSelect: React.FC<LocationFormSelectProps> = ({
  placeholder, options, value, onChange, hasError, disabled = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const btnRef = useRef<any>(null);

  const handleOpen = () => {
    if (disabled) return;
    if (open) { setOpen(false); return; }
    btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setDropPos({ top: y + h + 2, left: x, width: w });
      setOpen(true);
    });
  };

  return (
    <View>
      <TouchableOpacity
        ref={btnRef}
        style={[
          styles.btn,
          hasError && styles.btnError,
          disabled && styles.btnDisabled,
          isDark && { backgroundColor: colors.input.background, borderColor: colors.input.border },
          hasError && isDark && { borderColor: AdminColors.textError },
        ]}
        onPress={handleOpen}
        activeOpacity={0.8}
        disabled={disabled}
      >
        <Text style={[styles.val, !value && styles.placeholder, isDark && { color: value ? colors.text.primary : colors.text.disabled }]} numberOfLines={1}>
          {value || placeholder || 'Select…'}
        </Text>
        <Text style={[styles.arrow, isDark && { color: colors.text.secondary }]}>{open ? '▲' : '▼'}</Text>
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="none" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={styles.backdrop} onPress={() => setOpen(false)} activeOpacity={1} />
        <View style={[styles.dropdown, { top: dropPos.top, left: dropPos.left, minWidth: dropPos.width }, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
          <ScrollView
            bounces={false}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
            keyboardShouldPersistTaps="handled"
            style={styles.optionsList}
          >
            {options.map(opt => (
              <TouchableOpacity
                key={opt}
                style={[
                  styles.optRow,
                  value === opt && styles.optRowSelected,
                  isDark && value === opt && { backgroundColor: 'rgba(61,139,255,0.15)' },
                ]}
                onPress={() => { onChange(opt); setOpen(false); }}
                activeOpacity={0.7}
              >
                <Text style={[
                  styles.optTxt,
                  value === opt && styles.optTxtSelected,
                  isDark && { color: colors.text.primary },
                  isDark && value === opt && { color: colors.brand.primary },
                ]}>{opt}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  btn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 36,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 5,
    backgroundColor: AdminColors.bgSurface,
  },
  btnError: {
    borderColor: AdminColors.textError,
  },
  btnDisabled: {
    backgroundColor: AdminColors.gray25,
    opacity: 0.6,
  },
  val: {
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    flex: 1,
  },
  placeholder: {
    color: AdminColors.gray350,
  },
  arrow: {
    fontSize: 8,
    color: AdminColors.gray400,
    marginLeft: 6,
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
  },
  dropdown: {
    position: 'absolute',
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    paddingVertical: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 16,
  },
  optionsList: {
    maxHeight: 120,
  },
  optRow: {
    paddingVertical: 9,
    paddingHorizontal: 14,
  },
  optRowSelected: {
    backgroundColor: AdminColors.blue25,
  },
  optTxt: {
    fontSize: 13,
    fontFamily: FontFamily.regular,
    color: AdminColors.textPrimary,
  },
  optTxtSelected: {
    fontFamily: FontFamily.medium,
    color: AdminColors.blue700,
  },
});

export default LocationFormSelect;
